// A morning brief the user's own computer writes: the Brief widget keeps when
// and what (props.auto), the board sends the runner one turn at that time, and
// the runner's Claude Code or Codex writes the brief into the widget's stream.
// A computer that is off at that time writes it when it next connects that day.
// An Agent job, and a Number, Chart, List, Table or custom widget given a job,
// run the same way with the user's own words.

import type { WidgetType } from "./types";
import { modelFitsAgent } from "./agentModels";

export type BriefAgent = "claude" | "codex";

export interface BriefAuto {
  on: boolean;
  /** the time of day it is written, "HH:MM" in its own time zone */
  time: string;
  tz: string;
  agent: BriefAgent;
  /** what it covers: the board, news on a subject (empty for none), and anything else in the user's words */
  board: boolean;
  news: string;
  also: string;
  /** folders on the computer it may read, read-only, from those the user allowed there */
  folders: string[];
  /** when it was turned on or its time last changed: the first brief is the next one due after that */
  from: number;
  /** an Agent job's: how often (every day, one day a week, or only when the user presses Run), and what it asks */
  every: "day" | "week" | "manual";
  /** the day of the week for every: "week", 0 Sunday to 6 Saturday */
  day: number;
  prompt: string;
  /** a Button's "Don't ask before sending either" (Features 4): its hands-free runs send without asking too. Kept only
   * with noAsk, and only a Button's settings offer it; a job never sends hands-free */
  noAskSend?: true;
  /** Hands-free (Features 4; a Button's "Run without asking me" in Round 7): a Button's or job's runs never stop for a
   * tool's OK, but for one that sends, deletes or buys (shared/withoutAsking.ts). True or left out, and the user's alone,
   * as the rest of props.auto is: an agent or a coworker never sets it, and a shared link never sees it */
  noAsk?: true;
  /** the model it runs on, when it has its own (Features 4, 26 Oct); left out, it runs on its agent's default. The
   * user's alone, as the rest of props.auto is */
  model?: string;
}

export const BRIEF_DEFAULT: Omit<BriefAuto, "tz"> = { on: true, time: "08:00", agent: "claude", board: true, news: "", also: "", folders: [], from: 0, every: "day", day: 1, prompt: "" };

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function zoneOk(tz: unknown): tz is string {
  if (typeof tz !== "string" || !tz || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** A brief's settings as the board keeps them, or null when they are not a brief's. */
/**
 * Words an agent wrote for a Button to run, as the user reads them for their OK: characters that draw as nothing or
 * turn words round are taken out, since a hidden instruction can ride in tag or zero-width characters the user never
 * sees; line breaks and tabs stay (review, Round 7).
 */
export const visibleWords = (s: string): string => s.replace(/[\p{Cc}\p{Cf}]/gu, (c) => (c === "\n" || c === "\t" ? c : ""));

export function cleanAuto(raw: unknown): BriefAuto | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const words = (v: unknown, n: number) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, n) : "");
  return {
    on: r.on === true,
    time: typeof r.time === "string" && TIME.test(r.time) ? r.time : BRIEF_DEFAULT.time,
    tz: zoneOk(r.tz) ? r.tz : "UTC",
    agent: r.agent === "codex" ? "codex" : "claude",
    board: r.board !== false,
    news: words(r.news, 120),
    also: words(r.also, 600),
    folders: Array.isArray(r.folders) ? [...new Set(r.folders.filter((f): f is string => typeof f === "string" && f.length > 0 && f.length <= 400))].slice(0, 8) : [],
    from: typeof r.from === "number" && Number.isFinite(r.from) ? r.from : 0,
    every: r.every === "week" || r.every === "manual" ? r.every : "day",
    day: typeof r.day === "number" && Number.isInteger(r.day) && r.day >= 0 && r.day <= 6 ? r.day : 1,
    prompt: typeof r.prompt === "string" ? r.prompt.trim().slice(0, 4000) : "",
    ...(r.noAsk === true && r.noAskSend === true ? { noAskSend: true as const } : {}),
    ...(r.noAsk === true ? { noAsk: true as const } : {}),
    // a model of the other agent's is dropped: a Button switched from Claude to Codex runs on Codex's default
    ...(modelFitsAgent(r.agent === "codex" ? "codex" : "claude", r.model) ? { model: r.model as string } : {}),
  };
}

/**
 * A Button that comes from outside the board, pasted or in a board file (Round 7): its task waits on the user's yes,
 * as an agent's words do, and it never arrives hands-free, which the user turns on for one button.
 */
export function arrivedButton<T extends { kind: string; type?: string; props?: Record<string, unknown> }>(i: T): T {
  // a job or brief keeps what it does and when, and never arrives hands-free either: that is its owner's to turn on (Features 4)
  if (i.kind === "widget" && i.type !== "action" && i.props?.auto && typeof i.props.auto === "object" && ("noAsk" in i.props.auto || "noAskSend" in i.props.auto)) {
    const { noAsk: _free, noAskSend: _sends, ...auto } = i.props.auto as Record<string, unknown>;
    return { ...i, props: { ...i.props, auto } };
  }
  if (i.kind !== "widget" || i.type !== "action" || !i.props || !("auto" in i.props)) return i;
  const { auto, ...props } = i.props;
  const said = typeof props.task === "string" && props.task.trim() ? props.task : (cleanAuto(auto)?.prompt ?? "");
  return { ...i, props: { ...props, ...(said ? { task: said } : {}) } };
}

/** The date in a time zone, "YYYY-MM-DD". */
export function dayIn(ms: number, tz: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(ms);
}

/** How far a time zone is ahead of UTC at a moment, in ms. */
function offsetAt(ms: number, tz: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
      .formatToParts(ms)
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** The moment a day's brief is due: that day's time in its own time zone. */
export function dueOn(day: string, time: string, tz: string): number {
  const [y, m, d] = day.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  // twice, so a day the clocks change on lands on the right side of the change
  const first = guess - offsetAt(guess, tz);
  return guess - offsetAt(first, tz);
}

/**
 * Whether a brief is due now: on, past today's time, not yet written today,
 * and that time came after it was set: one turned on in the evening writes
 * its first brief the next morning. ran is the day it last ran, in its own
 * time zone.
 */
export function briefDue(a: BriefAuto, ran: string | null, now = Date.now()): boolean {
  if (!a.on || a.every === "manual") return false;
  const today = dayIn(now, a.tz);
  if (a.every === "week" && weekday(today) !== a.day) return false;
  const at = dueOn(today, a.time, a.tz);
  return ran !== today && now >= at && at >= a.from;
}

/** The day of the week of a date, 0 Sunday. */
function weekday(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** When it is next due: today's time if that is still to come, else tomorrow's. */
export function nextBrief(a: BriefAuto, ran: string | null, now = Date.now()): number | null {
  if (!a.on || a.every === "manual") return null;
  if (briefDue(a, ran, now)) return now;
  // the next day it runs on, today included while its time is still to come
  let day = dayIn(now, a.tz);
  for (let i = 0; i < 9; i++) {
    const at = dueOn(day, a.time, a.tz);
    if ((a.every === "day" || weekday(day) === a.day) && at > now && day !== ran && at >= a.from) return at;
    day = dayIn(at + 26 * 3_600_000, a.tz);
  }
  return null;
}

/** What the agent on the user's computer is asked to do, from the brief's settings. */
export function briefPrompt(a: BriefAuto, w: { id: string; title: string; stream: string }, board: string, folders: { path: string; write?: boolean }[]): string {
  const named = (list: { path: string }[]) => list.map((f) => `"${f.path}"`).join(" and ");
  const rw = folders.filter((f) => f.write);
  const ro = folders.filter((f) => !f.write);
  const covers = [
    a.board ? "what changed on the board since yesterday and what needs me (read it with read_board)" : "",
    a.news ? `today's news on ${a.news} (search the web, and name each source)` : "",
    folders.length
      ? `what matters in my files in ${named(folders)}, read with the board files tools${rw.length ? ` (${named(rw)} ${rw.length === 1 ? "is" : "are"} read and write: work there as the brief needs)` : ""}${ro.length ? ` (${named(ro)} ${ro.length === 1 ? "is" : "are"} read only)` : ""}`
      : "",
    a.also ? a.also : "",
  ].filter(Boolean);
  return [
    `Write today's morning brief for the Brief widget “${w.title || "Morning brief"}” (id ${w.id}) on my board “${board}”.`,
    `Cover ${covers.length ? covers.join("; ") : "what changed on the board and what needs me"}.`,
    "Keep it short and plain: a few headed sections in Markdown, the most important first, and end with what to do first today. Never make up a number.",
    `Then put it on the widget with push_data, stream "${w.stream}", text set to the brief. Change nothing else on the board.`,
  ].join(" ");
}

/** What the agent on the user's computer is asked to do for an Agent job: the user's own request, and where its answer goes. */
export function jobPrompt(a: BriefAuto, w: { id: string; title: string; stream: string; plain?: boolean }, board: string, folders: { path: string; write?: boolean }[]): string {
  const rw = folders.filter((f) => f.write).map((f) => `"${f.path}"`);
  const ro = folders.filter((f) => !f.write).map((f) => `"${f.path}"`);
  return [
    `This is the Agent job “${w.title || "Agent job"}” (widget id ${w.id}) on my board “${board}”. Do this: ${a.prompt || "tell me what changed on the board today"}`,
    folders.length ? `My project folders are there for it, with the board files tools${rw.length ? ` (${rw.join(" and ")} read and write)` : ""}${ro.length ? ` (${ro.join(" and ")} read only)` : ""}.` : "",
    // the user chose text for this job: no design
    w.plain
      ? `When you have the answer, put it on that widget with push_data, stream "${w.stream}", text set to the answer in short Markdown.`
      : `Show the answer so it reads at a glance. A few lines: put it on that widget with push_data, stream "${w.stream}", text set to the answer in short Markdown.`,
    w.plain
      ? ""
      : `An answer with a shape of its own (an inbox, leads, prices, a scoreboard) gets a design that fits it, not a wall of text, unless the widget already has one that fits (read_widget shows its html): call get_widget_guide, then update_widget with id ${w.id} and props.html alone, a small design in the board's widget kit that draws board.data; then push_data to stream "${w.stream}" with json in the shape your design reads, and text with the same answer in short Markdown.`,
    "Never make up a number. Change nothing else on the board unless the job asks you to.",
  ]
    .filter(Boolean)
    .join(" ");
}

/** The widgets a user can give a job: their job fills them, in the shape each reads. */
export const JOB_TYPES: readonly WidgetType[] = ["metric", "chart", "list", "table", "custom"];

export const canHaveJob = (type: WidgetType): boolean => JOB_TYPES.includes(type);

/** How many briefs and jobs one account runs on a schedule: Run now is on top, and never counts. */
export const MAX_SCHEDULED = 10;

/** Whether a widget runs on its own: a brief, an Agent job or a widget's job, turned on, with a day or a week to run on. */
export function runsOnSchedule(w: { kind: string; type?: WidgetType; props?: Record<string, unknown> }): boolean {
  if (w.kind !== "widget" || !w.type || !(w.type === "brief" || w.type === "job" || canHaveJob(w.type))) return false;
  const a = cleanAuto(w.props?.auto);
  return !!a?.on && a.every !== "manual";
}

/** What a scheduled run is refused with past the cap, on the server and in the job's settings. */
export const SCHEDULED_FULL = `Up to ${MAX_SCHEDULED} briefs and jobs run on a schedule for each account. Set one of them to run when you press Run now, or remove one, then try again.`;

/** The kinds of widget a job fills, as the user knows them. */
const LABELS: Partial<Record<WidgetType, string>> = { metric: "Number", chart: "Chart", list: "List", table: "Table" };

/** What each kind of widget is sent, in push_data's words. */
const SHAPES: Partial<Record<WidgetType, string>> = {
  metric: "value set to the one number (and unit, like $ or %, if it has one). Each run adds a point to its history",
  chart:
    "value set to today's number, which adds a point to its line. The first time, if the history is known, send it as points [{t, v}] with mode replace",
  list: "items set to the whole list, the most important first, each {label, value?, sub?, href?, status?}",
  table: "rows set to the whole table, one object per row with the same keys, which become its columns, and columns for their order",
  custom: "in the shape its code reads: read_widget first to see which of value, items, rows, text or json it uses",
};

/** What the agent on the user's computer is asked to do for a widget's job: the user's own words, and the shape its widget reads. */
export function widgetJobPrompt(a: BriefAuto, w: { id: string; title: string; type: WidgetType; stream: string }, board: string, folders: { path: string; write?: boolean }[]): string {
  const rw = folders.filter((f) => f.write).map((f) => `"${f.path}"`);
  const ro = folders.filter((f) => !f.write).map((f) => `"${f.path}"`);
  return [
    `This is the job of the widget “${w.title || "Untitled"}” (id ${w.id}) on my board “${board}”. Do this: ${a.prompt}`,
    folders.length ? `My project folders are there for it, with the board files tools${rw.length ? ` (${rw.join(" and ")} read and write)` : ""}${ro.length ? ` (${ro.join(" and ")} read only)` : ""}.` : "",
    `When you have the answer, put it on that widget with push_data, stream "${w.stream}", ${SHAPES[w.type] ?? "text set to the answer in short Markdown"}.`,
    w.type === "custom"
      ? "If its design does not suit the answer, rewrite it so it reads at a glance: call get_widget_guide, then update_widget with props.html, and send the data in the shape your design reads."
      : `If a ${LABELS[w.type] ?? "widget"} cannot show the answer well (an inbox summary in one number, say), give it a design that fits instead: call get_widget_guide, then update_widget with id ${w.id} and props.html, a small design in the board's widget kit that draws board.data (it becomes a widget of its own and keeps its job), and send the data in the shape your design reads. If it suits the answer, keep it as it is.`,
    "Never make up a number: if you cannot find one, send nothing for it and say why in your reply. Change nothing else on the board.",
  ]
    .filter(Boolean)
    .join(" ");
}
